/**
 * Artegustando - Hide floating chat/discount widgets when the mobile menu drawer is open.
 * Targets Shopify Inbox (and common chat widgets) via robust heuristics.
 */
(() => {
  const MOBILE_MAX = 990;

  const isMobile = () => window.matchMedia(`(max-width:${MOBILE_MAX}px)`).matches;

  function setHidden(el, hidden) {
    if (!el) return;
    el.style.setProperty('display', hidden ? 'none' : '', 'important');
  }

  function findInboxCandidates() {
    const candidates = new Set();

    // Custom elements that Shopify Inbox may inject
    document.querySelectorAll('shopify-inbox, shopify-chat, shopify-inbox-chat').forEach(el => candidates.add(el));

    // Iframes commonly used by Inbox/widgets
    document.querySelectorAll('iframe').forEach(ifr => {
      const src = (ifr.getAttribute('src') || '').toLowerCase();
      const title = (ifr.getAttribute('title') || '').toLowerCase();
      const id = (ifr.id || '').toLowerCase();
      const name = (ifr.name || '').toLowerCase();

      if (
        src.includes('inbox') ||
        src.includes('shopifychat') ||
        title.includes('inbox') ||
        id.includes('inbox') ||
        id.includes('shopifychat') ||
        name.includes('inbox')
      ) {
        candidates.add(ifr);
      }
    });

    // Heuristic A: elements explicitly mentioning inbox/chat
    document.querySelectorAll('body *').forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      const id = (el.id || '').toLowerCase();
      const cls = (el.className || '').toString().toLowerCase();
      const aria = (el.getAttribute('aria-label') || '').toLowerCase();
      const title = (el.getAttribute('title') || '').toLowerCase();

      if (
        id.includes('inbox') ||
        cls.includes('inbox') ||
        cls.includes('shopify-chat') ||
        id.includes('shopify-chat') ||
        id.includes('shopifychat') ||
        aria.includes('chat') ||
        aria.includes('assistenza') ||
        title.includes('chat')
      ) {
        candidates.add(el);
      }
    });

    // Heuristic B (most robust): fixed-position launchers in the bottom-left corner.
    // This catches widgets that don't expose recognizable IDs/classes (e.g., Shopify Inbox launcher with the orange hand).
    const viewportW = window.innerWidth || 0;
    const viewportH = window.innerHeight || 0;
    if (viewportW && viewportH) {
      document.querySelectorAll('iframe, button, a, div, span').forEach(el => {
        if (!(el instanceof HTMLElement)) return;

        // Never hide anything inside the menu drawer itself
        if (el.closest('.menu-drawer') || el.closest('.drawer') || el.closest('header-drawer')) return;

        const cs = window.getComputedStyle(el);
        if (!cs) return;
        if (cs.position !== 'fixed') return;
        if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return;

        // Quick reject: if it's huge, it's probably not a launcher
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        if (rect.width > 220 || rect.height > 220) return;

        // Bottom-left zone
        const nearLeft = rect.left >= 0 && rect.left <= 140;
        const nearBottom = rect.bottom >= viewportH - 220 && rect.bottom <= viewportH + 5;

        // Many widgets use very high z-index
        const z = parseInt(cs.zIndex || '0', 10);
        const highZ = !Number.isNaN(z) ? z >= 50 : false;

        if (nearLeft && nearBottom && highZ) {
          candidates.add(el);
        }
      });
    }

    return Array.from(candidates);
  }

  function hideOrShowWidgets(hide) {
    if (!isMobile()) hide = false;

    // Apply a body class so CSS can help too
    document.body.classList.toggle('ag-menu-drawer-open', hide);

    // Hide candidates and their closest fixed-position container if needed
    const candidates = findInboxCandidates();

    candidates.forEach(el => {
      // Try to hide a wrapper if present
      const wrapper = el.closest('div') || el;
      setHidden(wrapper, hide);
      setHidden(el, hide);
    });
  }

  function init() {
    // Prefer the *menu* drawer. Some themes also use <details class="drawer"> for other overlays
    // (cart, newsletter, etc.) so we target the header-drawer first.
    const getDrawer = () =>
      document.querySelector('header-drawer details.drawer') ||
      document.querySelector('header-drawer details') ||
      document.querySelector('details.menu-drawer-container') ||
      // Fallbacks
      document.querySelector('details.drawer > summary.main-menu-button')?.closest('details') ||
      document.querySelector('details.drawer') ||
      document.querySelector('details[id*="menu" i][id*="drawer" i]');

    const drawer = getDrawer();
    if (!drawer) return;

    const update = () => {
      const d = getDrawer() || drawer;
      const open = d && d.hasAttribute('open');
      hideOrShowWidgets(!!open);
    };

    // Listen on the document as well (some themes re-create the drawer DOM on resize)
    drawer.addEventListener('toggle', update);
    document.addEventListener('click', () => setTimeout(update, 0), { passive: true });
    update();

    // In case the widget loads after a delay, observe DOM and re-apply while open
    const mo = new MutationObserver(() => {
      const d = getDrawer() || drawer;
      if (d && d.hasAttribute('open')) hideOrShowWidgets(true);
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();